<script setup lang="ts">
import { ref, onMounted } from 'vue'

const emit = defineEmits<{
  (e: 'continue'): void
}>()

// 模拟月度数据变化
const changes = ref({
  money: { before: 98500, after: 128500 },
  fans: { before: 38200, after: 45800 },
  fame: { before: 58, after: 62 },
  reputation: { before: 68, after: 71 },
})

const incomeItems = [
  { label: '演出收入', amount: 85000 },
  { label: '商业合作', amount: 45000 },
  { label: '周边销售', amount: 12000 },
]

const expenseItems = [
  { label: '偶像薪资', amount: -35000 },
  { label: '设施维护', amount: -8000 },
  { label: '培训费用', amount: -15000 },
]

const showContent = ref(false)
const animatedValues = ref({
  money: 0,
  fans: 0,
  fame: 0,
  reputation: 0,
})

onMounted(() => {
  setTimeout(() => {
    showContent.value = true
    animateNumbers()
  }, 300)
})

function animateNumbers() {
  const duration = 800
  const startTime = Date.now()

  function update() {
    const elapsed = Date.now() - startTime
    const progress = Math.min(elapsed / duration, 1)
    const eased = 1 - Math.pow(1 - progress, 3) // ease-out cubic

    animatedValues.value = {
      money: Math.round(changes.value.money.before + (changes.value.money.after - changes.value.money.before) * eased),
      fans: Math.round(changes.value.fans.before + (changes.value.fans.after - changes.value.fans.before) * eased),
      fame: Math.round(changes.value.fame.before + (changes.value.fame.after - changes.value.fame.before) * eased),
      reputation: Math.round(changes.value.reputation.before + (changes.value.reputation.after - changes.value.reputation.before) * eased),
    }

    if (progress < 1) {
      requestAnimationFrame(update)
    }
  }

  update()
}

function formatNumber(n: number): string {
  if (n >= 10000) return (n / 10000).toFixed(1) + 'w'
  return n.toLocaleString()
}

const netIncome = incomeItems.reduce((sum, i) => sum + i.amount, 0) + expenseItems.reduce((sum, i) => sum + i.amount, 0)
</script>

<template>
  <div class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-bg-deep">
    <!-- 背景装饰 -->
    <div class="absolute inset-0 overflow-hidden pointer-events-none">
      <div class="absolute top-1/4 left-1/4 w-96 h-96 rounded-full bg-primary opacity-5 blur-3xl" />
      <div class="absolute bottom-1/4 right-1/4 w-80 h-80 rounded-full bg-accent-gold opacity-5 blur-3xl" />
    </div>

    <Transition name="result-slide">
      <div v-if="showContent" class="relative w-full max-w-md">
        <!-- 标题 -->
        <div class="text-center mb-6">
          <span class="text-4xl mb-2 block">📊</span>
          <h2 class="text-2xl font-display font-bold text-text-primary">第 3 月结算</h2>
          <p class="text-text-muted text-sm">事务所运营报告</p>
        </div>

        <!-- 主卡片 -->
        <div class="bg-bg-surface rounded-lg border border-accent-purple border-opacity-20 shadow-modal overflow-hidden">
          <!-- 核心数据变化 -->
          <div class="p-6 border-b border-accent-purple border-opacity-10">
            <div class="grid grid-cols-2 gap-4">
              <div class="text-center">
                <p class="text-text-muted text-xs mb-1">资金</p>
                <p class="text-2xl font-bold text-accent-gold tabular-nums">
                  ¥{{ formatNumber(animatedValues.money) }}
                </p>
                <p class="text-xs" :class="changes.money.after >= changes.money.before ? 'text-accent-cyan' : 'text-danger'">
                  {{ changes.money.after >= changes.money.before ? '+' : '' }}{{ formatNumber(changes.money.after - changes.money.before) }}
                </p>
              </div>
              <div class="text-center">
                <p class="text-text-muted text-xs mb-1">粉丝</p>
                <p class="text-2xl font-bold text-primary tabular-nums">
                  {{ formatNumber(animatedValues.fans) }}
                </p>
                <p class="text-xs text-accent-cyan">
                  +{{ formatNumber(changes.fans.after - changes.fans.before) }}
                </p>
              </div>
              <div class="text-center">
                <p class="text-text-muted text-xs mb-1">人气</p>
                <p class="text-2xl font-bold text-accent-cyan tabular-nums">
                  {{ animatedValues.fame }}
                </p>
                <p class="text-xs text-accent-cyan">+{{ changes.fame.after - changes.fame.before }}</p>
              </div>
              <div class="text-center">
                <p class="text-text-muted text-xs mb-1">信誉</p>
                <p class="text-2xl font-bold text-info tabular-nums">
                  {{ animatedValues.reputation }}
                </p>
                <p class="text-xs text-accent-cyan">+{{ changes.reputation.after - changes.reputation.before }}</p>
              </div>
            </div>
          </div>

          <!-- 收支明细 -->
          <div class="p-4">
            <h3 class="text-text-secondary text-xs font-medium mb-3">本月收支</h3>

            <!-- 收入 -->
            <div class="mb-3">
              <div v-for="item in incomeItems" :key="item.label" class="flex justify-between text-sm py-1">
                <span class="text-text-muted">{{ item.label }}</span>
                <span class="text-accent-cyan tabular-nums">+¥{{ formatNumber(item.amount) }}</span>
              </div>
            </div>

            <!-- 支出 -->
            <div class="mb-3">
              <div v-for="item in expenseItems" :key="item.label" class="flex justify-between text-sm py-1">
                <span class="text-text-muted">{{ item.label }}</span>
                <span class="text-danger tabular-nums">-¥{{ formatNumber(Math.abs(item.amount)) }}</span>
              </div>
            </div>

            <!-- 净收入 -->
            <div class="flex justify-between pt-3 border-t border-accent-purple border-opacity-10">
              <span class="text-text-primary font-medium">净收入</span>
              <span class="font-bold tabular-nums" :class="netIncome >= 0 ? 'text-accent-cyan' : 'text-danger'">
                {{ netIncome >= 0 ? '+' : '' }}¥{{ formatNumber(netIncome) }}
              </span>
            </div>
          </div>

          <!-- 下月预览 -->
          <div class="px-4 py-3 bg-bg-base">
            <p class="text-text-muted text-xs">
              📅 下月计划: 周年纪念公演 · 预计收入 ¥150,000
            </p>
          </div>
        </div>

        <!-- 继续按钮 -->
        <div class="mt-6">
          <button
            @click="emit('continue')"
            class="w-full py-3 bg-primary text-bg-deep font-semibold rounded-md hover:bg-primary-light transition-all shadow-glow-pink"
          >
            进入下月
          </button>
        </div>
      </div>
    </Transition>
  </div>
</template>

<style scoped>
.result-slide-enter-active {
  transition: opacity 0.3s ease, transform 0.4s cubic-bezier(0.16, 1, 0.3, 1);
}
.result-slide-leave-active {
  transition: opacity 0.2s ease;
}
.result-slide-enter-from {
  opacity: 0;
  transform: translateY(30px);
}
.result-slide-leave-to {
  opacity: 0;
}
</style>
